import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DELAY_THRESHOLD_MIN, NOTIFY_API_BASE, DURATION_FILTERS, KDS_LOAD_ALERT_CONFIG, KDS_COMPLETED_WINDOW_MIN, KDS_RELAXED_POLL_MS } from '../ticketConstants';
import { isStoreOpen, playDelayAlert, unlockKdsAudio, itemMatchesProfile, getItemColorForProfile, localDateISO } from '../ticketUtils';
import { computeDelayedItems } from '../ticketTimingUtils';
import { shouldRefetchOnWake, shouldNightlyReload } from '../lib/uptimeUtils';
import { mergeTicket, deriveTicketStatus, isRecentlyCompleted } from '../lib/kdsMerge';
import { computeKitchenLoad, nextAlertState, formatKitchenAlert, alertDedupKey, dedupWindowMs } from '../kdsLoadAlerts';
import { sqFsReopenForNewItems, sqFsStampItemFireTimes, sqFsRequestHelp, claimAlertSlot } from '../firestoreActions';
import { initFCM } from '../../../utils/fcmUtils';
import { useLiveOrders } from '../hooks/useLiveOrders';
import { useNewOrderAlert } from '../hooks/useNewOrderAlert';
import { useTicketChecks } from '../hooks/useTicketChecks';
import { useChefProfiles } from '../hooks/useChefProfiles';
import { useKdsSync } from '../../../utils/useKdsSync';
import { SyncBar } from '../components/SyncBar';
import { CompletedFilters } from '../components/CompletedFilters';
import { KdsCard } from '../components/KdsCard';
import { ChefProfileManager } from '../components/ChefProfileManager';
import { Stepper } from '../components/Stepper';
import { useLang, useAutoTranslate } from '../i18n';
import { isOnlineTicketName } from '../../../utils/onlineTicketName';

// Verbalize a ticket label for text-to-speech. Real names are read as-is; a
// short-id code (#A1B2C3) or a pure order number is prefixed with "number" and
// spelled out char-by-char so every digit/letter is announced clearly over
// kitchen noise (e.g. #A1B2C3 → "number A 1 B 2 C 3").
function spokenTicketLabel(label) {
  const raw = String(label || '').trim();
  if (!raw) return 'unnamed ticket';
  if (raw.startsWith('#')) return 'number ' + raw.slice(1).split('').join(' ');
  if (/^\d+$/.test(raw)) return 'number ' + raw.split('').join(' ');
  return raw;
}

// Shared "no delayed items" value, so completed cards always get the same array.
const NO_DELAYS = Object.freeze([]);

// How long a summary highlight stays on the board before auto-clearing.
const HIGHLIGHT_AUTO_CLEAR_MS = 20000;

// Self-contained auto-clear countdown for the summary focus pill. It owns its
// own timer so ticking never re-renders the (potentially large) board — only
// this tiny component. Uses wall-clock time so it stays accurate even if the
// tab is throttled, and calls onExpire() at zero. Remount it (via `key`) to
// restart the countdown for a newly selected item.
function HighlightCountdown({ durationMs = HIGHLIGHT_AUTO_CLEAR_MS, onExpire }) {
  const [msLeft, setMsLeft] = useState(durationMs);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const left = Math.max(0, durationMs - (Date.now() - start));
      setMsLeft(left);
      if (left <= 0) { clearInterval(id); onExpire?.(); }
    }, 250);
    return () => clearInterval(id);
  }, [durationMs, onExpire]);
  const pct = Math.max(0, Math.min(100, (msLeft / durationMs) * 100));
  const sec = Math.ceil(msLeft / 1000);
  const low = msLeft <= 6000;
  return (
    <span className={`kds__countdown${low ? ' kds__countdown--low' : ''}`} role="timer"
      aria-label={`Highlight clears in ${sec} second${sec !== 1 ? 's' : ''}`}>
      <span className="kds__countdown-track">
        <span className="kds__countdown-fill" style={{ width: pct + '%' }} />
      </span>
      <span className="kds__countdown-sec">{sec}s</span>
    </span>
  );
}

/**
 * Auto-refresh pin (dedicated KDS screens).
 *
 * By default the board follows STORE_HOURS: poll every 10s while open, pause when
 * closed — which includes the afternoon lunch→dinner gap. On an always-on kitchen
 * display that pause is wrong, so an explicitly chosen interval is PINNED to
 * localStorage and survives reloads/reboots. No pin (key absent) = follow hours.
 */
const POLL_PIN_KEY = 'td_kds_poll';

/**
 * Board density and legibility — pinned per device, so a station keeps the setup
 * that suits its screen and viewing distance.
 *
 * These are deliberately two settings, the way commercial kitchen displays do
 * it. Fusing them into one "zoom" meant the only way to fit another ticket was
 * to shrink the text, which also shrank the action buttons out of glove range.
 */
const COLS_KEY = 'td_kds_cols';
const TEXT_KEY = 'td_kds_text';
const LEGACY_ZOOM_KEY = 'td_kds_zoom';   // pre-split, meant both at once
const COLS_MIN = 1, COLS_MAX = 8;
/** Text only. The action buttons divide their min-height by this scale, so the
 *  tap target stays the same physical size at every step (see .kds__btn). */
const TEXT_STEPS = [
  { label: 'S',  scale: 0.85 },
  { label: 'M',  scale: 1 },
  { label: 'L',  scale: 1.2 },
  { label: 'XL', scale: 1.45 },
];
const TEXT_DEFAULT_IDX = 1;   // 'M' — the size the board has always shipped at

/** Explicit column count, or null to follow the breakpoint (Auto). */
function readColsPref() {
  try {
    const n = parseInt(localStorage.getItem(COLS_KEY), 10);
    return Number.isFinite(n) && n >= COLS_MIN && n <= COLS_MAX ? n : null;
  } catch { return null; }
}

/** Text step, carrying a pre-split zoom over to the nearest size. */
function readTextPref() {
  try {
    const saved = parseInt(localStorage.getItem(TEXT_KEY), 10);
    if (Number.isFinite(saved) && saved >= 0 && saved < TEXT_STEPS.length) return saved;
    const legacy = parseFloat(localStorage.getItem(LEGACY_ZOOM_KEY));
    if (!Number.isFinite(legacy)) return TEXT_DEFAULT_IDX;
    return TEXT_STEPS.reduce((best, s, i) =>
      Math.abs(s.scale - legacy) < Math.abs(TEXT_STEPS[best].scale - legacy) ? i : best, TEXT_DEFAULT_IDX);
  } catch { return TEXT_DEFAULT_IDX; }
}

/** Nightly unattended restart: local hour to restart in, and the once-per-day stamp. */
const NIGHTLY_RELOAD_HOUR = 4;
const RELOAD_DAY_KEY = 'td_kds_reload_day';
function readPinnedPoll() {
  try {
    const raw = localStorage.getItem(POLL_PIN_KEY);
    if (raw == null || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;   // 0 = deliberately Off
  } catch { return null; }
}

export function KdsBoardTab({ focusMode, toggleFocus, refetchRef, helpRef, onOpenDailyOps }) {
  const { t, ln } = useLang();
  const [now, setNow] = useState(new Date());
  const [view, setView] = useState('active');
  const [chefMode, setChefMode] = useState(() => {
    try { return localStorage.getItem('td_chef_mode') === '1'; } catch { return false; }
  });
  const [completedFilters, setCompletedFilters] = useState({ duration: 'all', type: 'all', sort: 'newest', dateFrom: '', dateTo: '' });
  // A pinned interval wins over store-hours mode and survives a reload/reboot.
  const [pollPinned, setPollPinned] = useState(() => readPinnedPoll() != null);
  const [pollInterval, setPollInterval] = useState(() => {
    const pinned = readPinnedPoll();
    return pinned != null ? pinned : (isStoreOpen() ? 10000 : 0);
  });

  // ── New-order alerts (chime + browser/FCM push) ──────────────────────────
  // enabled: master switch; muted: keeps push but silences the chime.
  const [alertsEnabled, setAlertsEnabled] = useState(() => {
    try { return localStorage.getItem('kds_new_order_alerts') === '1'; } catch { return false; }
  });
  const [alertsMuted, setAlertsMuted] = useState(() => {
    try { return localStorage.getItem('kds_new_order_muted') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('kds_new_order_alerts', alertsEnabled ? '1' : '0'); } catch {} }, [alertsEnabled]);
  useEffect(() => { try { localStorage.setItem('kds_new_order_muted', alertsMuted ? '1' : '0'); } catch {} }, [alertsMuted]);

  // Enabling must happen inside a click so the browser lets us unlock audio
  // and prompt for notification permission. Also registers this device for FCM.
  const enableAlerts = useCallback(async () => {
    unlockKdsAudio();
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        await Notification.requestPermission();
      }
    } catch {}
    initFCM().catch(() => {}); // caches an FCM token for off-device push (no-op if VAPID key unset)
    setAlertsEnabled(true);
    setAlertsMuted(false);
  }, []);

  const toggleAlerts = useCallback(() => {
    if (!alertsEnabled) { enableAlerts(); return; }
    // Already on → cycle: on → muted → off
    if (!alertsMuted) { setAlertsMuted(true); return; }
    setAlertsEnabled(false);
    setAlertsMuted(false);
  }, [alertsEnabled, alertsMuted, enableAlerts]);
  // ── Density and legibility are two separate controls ──────────────────────
  // One "zoom" used to mean both, so the only way to fit another ticket was to
  // shrink the text — which dragged the action buttons down with it and still
  // could not add a column on a narrow screen. Columns now sets how many
  // tickets share a row; text size only sets how big the words are.
  const [cols, setCols] = useState(readColsPref);
  const [textIdx, setTextIdx] = useState(readTextPref);
  const textScale = TEXT_STEPS[textIdx].scale;
  const gridRef = useRef(null);
  const autoColsRef = useRef(null);
  const [belowCount, setBelowCount] = useState(0);

  const stepCols = useCallback((delta) => {
    setCols(prev => {
      // Stepping up from Auto starts at whatever Auto is currently showing, so
      // the first press always visibly changes something.
      const from = prev ?? autoColsRef.current ?? 2;
      const next = Math.min(COLS_MAX, Math.max(COLS_MIN, from + delta));
      try { localStorage.setItem(COLS_KEY, String(next)); } catch {}
      return next;
    });
  }, []);
  const resetCols = useCallback(() => {
    setCols(null);
    try { localStorage.removeItem(COLS_KEY); } catch {}
  }, []);
  const stepText = useCallback((delta) => {
    setTextIdx(prev => {
      const next = Math.min(TEXT_STEPS.length - 1, Math.max(0, prev + delta));
      try { localStorage.setItem(TEXT_KEY, String(next)); } catch {}
      return next;
    });
  }, []);
  const resetText = useCallback(() => {
    setTextIdx(TEXT_DEFAULT_IDX);
    try { localStorage.setItem(TEXT_KEY, String(TEXT_DEFAULT_IDX)); } catch {}
  }, []);

  // Board-level "Need Help"
  const [boardHelpSent, setBoardHelpSent] = useState(false);
  const [boardHelpCooldown, setBoardHelpCooldown] = useState(0);
  useEffect(() => {
    if (helpRef?.current) {
      helpRef.current._setSent?.(boardHelpSent);
      helpRef.current._setCooldown?.(boardHelpCooldown);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardHelpSent, boardHelpCooldown]);

  const handleBoardNeedHelp = useCallback(async () => {
    if (boardHelpSent || boardHelpCooldown > 0) return;
    setBoardHelpSent(true);
    setBoardHelpCooldown(60);
    const iv = setInterval(() => setBoardHelpCooldown(prev => {
      if (prev <= 1) { clearInterval(iv); return 0; }
      return prev - 1;
    }), 1000);
    try {
      await sqFsRequestHelp({ orderId: 'board', ticketName: 'General Help', employeeName: 'Staff', message: 'Staff needs assistance at the truck' });
    } catch {}
    setTimeout(() => setBoardHelpSent(false), 5000);
  }, [boardHelpSent, boardHelpCooldown]);

  // Hidden tickets in chef mode
  const [hiddenTickets, setHiddenTickets] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('kds_hidden_tickets') || '[]')); } catch { return new Set(); }
  });
  const [showHidden, setShowHidden] = useState(false);
  const hideTicket = useCallback((id) => {
    setHiddenTickets(prev => {
      const next = new Set(prev); next.add(id);
      try { localStorage.setItem('kds_hidden_tickets', JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);
  const unhideAll = useCallback(() => {
    setHiddenTickets(new Set());
    setShowHidden(false);
    try { localStorage.removeItem('kds_hidden_tickets'); } catch {}
  }, []);

  // Chef profiles
  const { profiles: chefProfiles, setProfiles: setChefProfiles } = useChefProfiles();
  const [activeProfileId, setActiveProfileId] = useState(() => {
    try { return localStorage.getItem('kds_active_profile') || null; } catch { return null; }
  });
  const [showProfileMgr, setShowProfileMgr] = useState(false);
  const activeProfile = chefProfiles.find(p => p.id === activeProfileId) || null;
  const handleSelectProfile = (id) => {
    setActiveProfileId(id);
    try { if (id) localStorage.setItem('kds_active_profile', id); else localStorage.removeItem('kds_active_profile'); } catch {}
  };

  // Auto-set interval based on store hours (skipped while an interval is pinned).
  const userOverrideRef = useRef(pollPinned);
  useEffect(() => { userOverrideRef.current = pollPinned; }, [pollPinned]);
  /** Pick an explicit interval → pin it (persists across reloads). */
  const handleSetPollInterval = useCallback((v) => {
    setPollPinned(true);
    setPollInterval(v);
    try { localStorage.setItem(POLL_PIN_KEY, String(v)); } catch { /* private mode */ }
  }, []);
  /** Back to following store hours — clears the pin. */
  const handleAutoPoll = useCallback(() => {
    setPollPinned(false);
    try { localStorage.removeItem(POLL_PIN_KEY); } catch { /* private mode */ }
    setPollInterval(isStoreOpen() ? 10000 : 0);
  }, []);
  useEffect(() => {
    const id = setInterval(() => { if (!userOverrideRef.current) setPollInterval(isStoreOpen() ? 10000 : 0); }, 60000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => { try { localStorage.setItem('td_chef_mode', chefMode ? '1' : '0'); } catch {} }, [chefMode]);

  // Item Summary panel
  const [showItemSummary, setShowItemSummary] = useState(() => {
    try { return localStorage.getItem('td_item_summary') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('td_item_summary', showItemSummary ? '1' : '0'); } catch {} }, [showItemSummary]);

  // Live orders
  // Auto mode relaxes the backup poll once webhooks are proven to reach this
  // board; a pinned interval is always honoured as chosen.
  const { orders, loading, error, lastFetch, refetch, updateSource, pollMs } =
    useLiveOrders(pollInterval, { relaxedPollMs: pollPinned ? 0 : KDS_RELAXED_POLL_MS });
  // Chime + browser/FCM push whenever a brand-new order lands on the board.
  useNewOrderAlert(orders, { enabled: alertsEnabled, muted: alertsMuted });
  useEffect(() => { if (refetchRef) refetchRef.current = refetch; }, [refetch, refetchRef]);
  useEffect(() => {
    if (helpRef) {
      if (!helpRef.current) helpRef.current = {};
      helpRef.current.fn = handleBoardNeedHelp;
    }
  }, [helpRef, handleBoardNeedHelp]);
  const orderIds = useMemo(() => orders.map(o => o.id), [orders]);
  const checks = useTicketChecks(orderIds);

  // LAN / Offline sync
  const [wsChecksOverlay, setWsChecksOverlay] = useState({});
  const handleWsPatch = useCallback((orderId, patch) => {
    setWsChecksOverlay(prev => ({ ...prev, [orderId]: { ...(prev[orderId] || {}), ...patch } }));
  }, []);
  const { lanOnline, netOnline, broadcast } = useKdsSync({ onPatch: handleWsPatch });
  useEffect(() => {
    const ids = Object.keys(checks);
    if (!ids.length) return;
    setWsChecksOverlay(prev => {
      if (!ids.some(id => id in prev)) return prev;
      const next = { ...prev }; ids.forEach(id => delete next[id]); return next;
    });
  }, [checks]);

  // Merge orders + checks + WS overlay (pure logic lives in lib/kdsMerge).
  // A ticket whose order, check data and overlay are all the same objects as last
  // time keeps its merged object, so one ticket changing re-renders one card, not
  // the whole board. (useLiveOrders and useTicketChecks both keep unchanged
  // entries' identity.) Only fields computed from those inputs are cached —
  // anything clock-dependent is derived from `now` at render (deriveTicketStatus).
  const mergeCacheRef = useRef(new Map());
  const merged = useMemo(() => {
    const nowMs = Date.now();
    const prev = mergeCacheRef.current;
    const next = new Map();
    const list = orders.map(o => {
      const ck = checks[o.id];
      const ov = wsChecksOverlay[o.id];
      const hit = prev.get(o.id);
      const ticket = hit && hit.o === o && hit.ck === ck && hit.ov === ov
        ? hit.ticket
        : mergeTicket(o, { ...(ck || {}), ...(ov || {}) }, nowMs);
      next.set(o.id, { o, ck, ov, ticket });
      return ticket;
    });
    mergeCacheRef.current = next;
    return list;
  }, [orders, checks, wsChecksOverlay]);

  useEffect(() => { const id = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(id); }, []);

  // ── Wake / visibility resync ────────────────────────────────────────────────
  // Browsers throttle (or fully suspend) timers in a hidden tab and across OS
  // sleep, so on wake BOTH the clock and the order list can be stale. Snap `now`
  // forward immediately and pull fresh orders — but only if the data is actually
  // old, so a quick alt-tab doesn't hammer the Square API.
  const lastFetchRef = useRef(null);
  useEffect(() => { lastFetchRef.current = lastFetch ? lastFetch.getTime() : null; }, [lastFetch]);
  useEffect(() => {
    const onWake = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      setNow(new Date());
      if (shouldRefetchOnWake({ lastFetchMs: lastFetchRef.current, nowMs: Date.now() })) refetch();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
    };
  }, [refetch]);

  // Auto-reopen for new items
  const reopenInFlightRef = useRef(new Set());
  useEffect(() => {
    merged.forEach(ticket => {
      if (!(ticket.id in checks)) return;   // its ticketChecks data hasn't arrived yet
      if (ticket.hasNewItems && !reopenInFlightRef.current.has(ticket.id)) {
        reopenInFlightRef.current.add(ticket.id);
        sqFsReopenForNewItems(ticket.id, ticket.checkedItems, ticket.items, ticket.createdAt)
          .catch(() => {})
          .finally(() => reopenInFlightRef.current.delete(ticket.id));
      }
    });
  // `now` (30 s) retries a write that failed: `merged` no longer changes on
  // every poll, which used to re-run this implicitly.
  }, [merged, checks, now]);

  // Stamp fire times for items added to an ACTIVE ticket. Establishes a baseline
  // on first sight (items = createdAt) so later additions are detected and given
  // their own fire time (= now) instead of inheriting the order's original time.
  // Completed tickets are handled by the reopen flow above, so they're skipped.
  const stampInFlightRef = useRef(new Set());
  useEffect(() => {
    merged.forEach(ticket => {
      // Wait for the ticket's own data: before it arrives every ticket looks
      // unstamped, and stamping would read each one from the server (a billed
      // read and a new listener apiece) only to find it already stamped.
      if (!(ticket.id in checks)) return;
      if (ticket.manuallyCompleted || ticket.hasNewItems) return;
      const itemCount = (ticket.items || []).length;
      if (itemCount > ticket.persistedFireCount && !stampInFlightRef.current.has(ticket.id)) {
        stampInFlightRef.current.add(ticket.id);
        sqFsStampItemFireTimes(ticket.id, ticket.items, ticket.createdAt)
          .catch(() => {})
          .finally(() => stampInFlightRef.current.delete(ticket.id));
      }
    });
  }, [merged, checks, now]);   // `now`: retry a failed stamp, as above

  const deriveStatus = o => deriveTicketStatus(o, now.getTime());

  const active = useMemo(() => merged.filter(o => { const s = deriveStatus(o); return s === 'open' || s === 'inprogress'; }).sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)), [merged, now]); // eslint-disable-line
  const stale = useMemo(() => merged.filter(o => deriveStatus(o) === 'stale'), [merged, now]); // eslint-disable-line
  const completedAll = useMemo(() => merged.filter(o => deriveStatus(o) === 'completed'), [merged, now]); // eslint-disable-line
  // The Completed tab keeps only the last KDS_COMPLETED_WINDOW_MIN of finished
  // orders so the live board stays about what's happening now; the whole day is
  // in the History tab, and "Show all today" brings the rest back here (e.g. to
  // reopen an older order). Every ticket stays watched either way — a quiet
  // finished ticket costs no reads — so a reopen from another screen still lands.
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  const completedRecent = useMemo(
    () => completedAll.filter(o => isRecentlyCompleted(o, now.getTime(), KDS_COMPLETED_WINDOW_MIN)),
    [completedAll, now]);
  const completedShown = showAllCompleted ? completedAll : completedRecent;
  const completedEarlier = completedAll.length - completedRecent.length;
  const windowHours = Math.round(KDS_COMPLETED_WINDOW_MIN / 60);

  // ── Nightly self-restart (unattended kitchen screen) ────────────────────────
  // A tab left up for weeks never picks up a deploy and can drift. Restart once a
  // night — but ONLY while idle (no active tickets), so service is never
  // interrupted. Checked on the 30s tick. The day stamp must be provably
  // persisted first: without it we could never dedupe and would reload in a loop.
  useEffect(() => {
    let lastReloadDay = null;
    try { lastReloadDay = localStorage.getItem(RELOAD_DAY_KEY); } catch { return; }
    if (!shouldNightlyReload({ now, reloadHour: NIGHTLY_RELOAD_HOUR, lastReloadDay, activeCount: active.length })) return;
    const today = localDateISO(now);
    try {
      localStorage.setItem(RELOAD_DAY_KEY, today);
      if (localStorage.getItem(RELOAD_DAY_KEY) !== today) return; // can't dedupe → never risk a loop
    } catch { return; }
    window.location.reload();
  }, [now, active.length]);

  // Delay alert sound — uses per-item fire times
  const delayAlertedAt = useRef({});
  useEffect(() => {
    const REPEAT_INTERVAL_MS = 2 * 60 * 1000;
    const nowMs = Date.now();
    active.forEach(ticket => {
      if (ticket.manuallyCompleted) return;
      // Until its check data arrives a finished ticket looks active and overdue —
      // opening the board mid-service used to beep for orders already done.
      if (!(ticket.id in checks)) return;
      const delayed = computeDelayedItems({
        itemCount: (ticket.items || []).length,
        itemFireTimes: ticket.itemFireTimes,
        checkedItems: ticket.checkedItems || [],
        nowMs,
        thresholdMin: DELAY_THRESHOLD_MIN,
      });
      if (delayed.length === 0) return;
      const last = delayAlertedAt.current[ticket.id] || 0;
      if (nowMs - last >= REPEAT_INTERVAL_MS) {
        delayAlertedAt.current[ticket.id] = nowMs;
        playDelayAlert();
      }
    });
    const activeIds = new Set(active.map(t => t.id));
    Object.keys(delayAlertedAt.current).forEach(id => { if (!activeIds.has(id)) delete delayAlertedAt.current[id]; });
  }, [active, now]); // eslint-disable-line

  // ── Kitchen-load Telegram alerts (edge-triggered, hysteresis, heartbeat) ──
  // A symptom-based state machine: alert when the kitchen is genuinely behind,
  // message only on level changes, re-ping while critical, and send one
  // "caught up" on recovery. State is seeded silently so opening mid-rush never
  // fires a fresh alert. See kdsLoadAlerts.js for the design rationale.
  const loadAlertRef = useRef(null);
  const sendLoadAlert = useCallback(async (action, state, metrics) => {
    // Cross-device dedup: only the first tab/device to claim the shared slot for
    // this logical alert actually sends. Every device's state machine still
    // advances locally; this only gates the network send.
    const won = await claimAlertSlot(alertDedupKey(action, state.level), dedupWindowMs(action, KDS_LOAD_ALERT_CONFIG));
    if (!won) return;
    const incidentMinutes = state.incidentId ? Math.round((Date.now() - state.incidentId) / 60000) : 0;
    const { title, body, severity } = formatKitchenAlert(action, state.level, metrics, { incidentMinutes });
    const payload = {
      // incidentId groups an incident's messages; lastNotifyMs keeps each send unique.
      alertKey: `kds_load_${state.incidentId || 'recovery'}_${state.lastNotifyMs}`,
      title, body, severity, channels: ['telegram'], fcmTokens: [],
    };
    fetch(NOTIFY_API_BASE + '/api/send-notification', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Only evaluate while the store is open; reset state when closed so reopening
    // seeds a fresh baseline instead of carrying a stale incident across the gap.
    if (!isStoreOpen()) { loadAlertRef.current = null; return; }
    const nowMs = now.getTime();
    const metrics = computeKitchenLoad(active, nowMs, KDS_LOAD_ALERT_CONFIG);
    const { state, action } = nextAlertState(loadAlertRef.current, metrics, KDS_LOAD_ALERT_CONFIG, nowMs);
    loadAlertRef.current = state;
    if (action !== 'none') sendLoadAlert(action, state, metrics);
  }, [active, now, sendLoadAlert]); // eslint-disable-line

  const completed = useMemo(() => {
    const { duration, type, sort, dateFrom, dateTo } = completedFilters;
    let list = [...completedShown];
    if (dateFrom) { const f = new Date(dateFrom); list = list.filter(o => new Date(o.createdAt || 0) >= f); }
    if (dateTo) { const t2 = new Date(dateTo); list = list.filter(o => new Date(o.createdAt || 0) <= t2); }
    if (duration !== 'all') { const df = DURATION_FILTERS.find(f => f.key === duration); if (df) list = list.filter(o => o.timeTakenMin != null && df.test(o.timeTakenMin)); }
    if (type !== 'all') {
      list = list.filter(o => {
        const name = (o.ticketName || o.customerName || '').toLowerCase();
        const isTogo = name.includes('togo') || name.includes('to-go') || name.includes('to go');
        // Same typo-tolerant check as the Square dashboard ("onlibe", "pnline"…),
        // plus orders placed through our own online ordering page.
        const isOnline = !!o.isOnlineOrder || isOnlineTicketName(name);
        if (type === 'togo') return isTogo;
        if (type === 'online') return isOnline;
        if (type === 'other') return !isTogo && !isOnline;
        return true;
      });
    }
    if (sort === 'newest') list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    if (sort === 'oldest') list.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    if (sort === 'fastest') list.sort((a, b) => (a.timeTakenMin ?? 999) - (b.timeTakenMin ?? 999));
    if (sort === 'slowest') list.sort((a, b) => (b.timeTakenMin ?? 0) - (a.timeTakenMin ?? 0));
    return list.slice(0, 50);
  }, [completedShown, completedFilters]);

  const counts = { active: active.length, stale: stale.length, completed: completedShown.length };
  const showList = view === 'active' ? active : view === 'stale' ? stale : completed;
  const visibleList = useMemo(
    () => ((chefMode && !showHidden) ? showList.filter(t => !hiddenTickets.has(t.id)) : showList),
    [showList, chefMode, showHidden, hiddenTickets]);

  // Per-card delay flags, cached per ticket object and clock tick so a card whose
  // ticket didn't change gets the very same array and can skip re-rendering.
  const delayCacheRef = useRef(new Map());
  const delayFor = useMemo(() => {
    const nowMs = now.getTime();
    const prev = delayCacheRef.current;
    const next = new Map();
    visibleList.forEach(tk => {
      const hit = prev.get(tk.id);
      if (hit && hit.tk === tk && hit.nowMs === nowMs) { next.set(tk.id, hit); return; }
      const tItems = tk.items || [];
      const tChecked = tk.checkedItems || [];
      // Use item-level fire times for delay detection
      const delayedItemIdxs = !tk.manuallyCompleted
        ? computeDelayedItems({
            itemCount: tItems.length,
            itemFireTimes: tk.itemFireTimes,
            checkedItems: tChecked,
            nowMs,
            thresholdMin: DELAY_THRESHOLD_MIN,
          })
        : NO_DELAYS;
      const uncheckedIdxs = tItems.map((_, i) => i).filter(i => !tChecked.includes(i));
      const isDelayed = !tk.manuallyCompleted && uncheckedIdxs.length > 0 && uncheckedIdxs.every(i => delayedItemIdxs.includes(i));
      next.set(tk.id, { tk, nowMs, delayedItemIdxs, isDelayed });
    });
    delayCacheRef.current = next;
    return next;
  }, [visibleList, now]);
  const hiddenCount = chefMode ? showList.filter(t => hiddenTickets.has(t.id)).length : 0;

  // ── Off-screen queue depth ────────────────────────────────────────────────
  // The board scrolls, so a ticket below the fold is invisible — and an
  // invisible ticket is an unmade order. Count what's below and offer one tap
  // to page down to it. IntersectionObserver keeps this off the scroll path.
  const boardIdsKey = visibleList.map(tk => tk.id).join(',');
  /** The scroll container: focus mode owns its own, otherwise it's the page. */
  const getScroller = useCallback(
    () => (focusMode ? gridRef.current?.closest('.td__page--focus') || null : null),
    [focusMode]);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof IntersectionObserver === 'undefined') { setBelowCount(0); return; }
    const below = new Set();
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        const foldY = e.rootBounds ? e.rootBounds.bottom : window.innerHeight;
        if (!e.isIntersecting && e.boundingClientRect.top >= foldY) below.add(e.target);
        else below.delete(e.target);
      });
      setBelowCount(below.size);
    }, { root: getScroller(), threshold: 0 });
    Array.from(grid.children).forEach(c => io.observe(c));
    return () => io.disconnect();
    // Re-observe when the cards themselves change, or when a layout control
    // moves them across the fold.
  }, [boardIdsKey, getScroller, cols, textScale, view, showItemSummary]);

  const pageDown = useCallback(() => {
    const scroller = getScroller();
    const target = scroller || window;
    const viewportH = (scroller ? scroller.clientHeight : window.innerHeight) || 600;
    target.scrollBy({ top: Math.round(viewportH * 0.82), behavior: 'smooth' });
  }, [getScroller]);

  // What Auto currently resolves to, so stepping off Auto starts from the count
  // already on screen instead of jumping.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const tpl = getComputedStyle(grid).gridTemplateColumns;
      autoColsRef.current = tpl && tpl !== 'none' ? tpl.split(' ').filter(Boolean).length : null;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(grid);
    return () => ro.disconnect();
  }, [boardIdsKey, cols]);

  // Bulk-prep item summary — available in any view, not just Chef view.
  const itemSummary = useMemo(() => {
    if (!showItemSummary) return [];
    const map = {};
    // Only drop hidden tickets when Chef view is actively hiding them (keeps the summary in sync with the board).
    const sorted = [...active].filter(t => !(chefMode && !showHidden && hiddenTickets.has(t.id))).sort((a, b) => {
      return (a.createdAt ? new Date(a.createdAt).getTime() : 0) - (b.createdAt ? new Date(b.createdAt).getTime() : 0);
    });
    sorted.forEach(t => {
      const tMs = t.createdAt ? new Date(t.createdAt).getTime() : Date.now();
      const items2 = t.items || [];
      const chefDone2 = new Set(t.chefDoneItems || []);
      const frontDone = new Set(t.checkedItems || []);
      const tLabel = t.ticketName || t.customerName || `#${t.shortId}`;
      items2.forEach((it, idx) => {
        if (chefDone2.has(idx) || frontDone.has(idx)) return;
        const raw = it.name || 'Unknown';
        const key = raw.trim().toLowerCase();
        const qty = it.qty || 1;
        if (!map[key]) map[key] = { key, displayName: raw, totalQty: 0, earliestMs: tMs, tickets: [] };
        const row = map[key];
        row.totalQty += qty;
        if (tMs < row.earliestMs) row.earliestMs = tMs;
        const existing = row.tickets.find(x => x.id === t.id);
        if (existing) existing.qty += qty;
        else row.tickets.push({ id: t.id, label: tLabel, qty });
      });
    });
    return Object.values(map).sort((a, b) => a.earliestMs - b.earliestMs || a.displayName.localeCompare(b.displayName));
  }, [active, showItemSummary, chefMode, showHidden, hiddenTickets]);

  // Auto-translate the summary's item names into the active language (no-op in English).
  useAutoTranslate(itemSummary.map(r => r.displayName));

  // Bulk-prep summary interaction: tap an item chip to highlight every ticket
  // that contains it and speak the item + ticket names aloud (hands-free for the
  // line cook). Tap the same chip again to clear.
  const [selectedItemKey, setSelectedItemKey] = useState(null);
  const highlightedTicketIds = useMemo(() => {
    if (!selectedItemKey) return null;
    const row = itemSummary.find(r => r.key === selectedItemKey);
    return row ? new Set(row.tickets.map(t => t.id)) : null;
  }, [selectedItemKey, itemSummary]);
  // Drop the selection once the item clears out (all its tickets done/hidden).
  useEffect(() => {
    if (selectedItemKey && !itemSummary.some(r => r.key === selectedItemKey)) setSelectedItemKey(null);
  }, [itemSummary, selectedItemKey]);

  const announceSummaryItem = useCallback((row) => {
    try {
      if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
      const synth = window.speechSynthesis;
      const labels = row.tickets.map(t => spokenTicketLabel(t.label));
      const n = labels.length;
      const ticketPhrase = n
        ? `${n} ticket${n !== 1 ? 's' : ''}: ${labels.join(', ')}`
        : 'no active tickets';
      const speak = () => {
        const utter = new SpeechSynthesisUtterance(`${row.displayName}. ${ticketPhrase}.`);
        utter.rate = 0.95;
        synth.speak(utter);
      };
      // Chrome truncates an utterance to its first sentence when speak() is called
      // synchronously right after cancel() — which is why only the item name was
      // announced. Cancel only when something is already queued, then defer the new
      // utterance a tick so the queue is genuinely clear before we speak.
      if (synth.speaking || synth.pending) {
        synth.cancel();
        setTimeout(speak, 130);
      } else {
        speak();
      }
    } catch {}
  }, []);

  const handleSummaryItemClick = useCallback((row) => {
    setSelectedItemKey(prev => {
      if (prev === row.key) return null;   // tap again to clear the highlight
      announceSummaryItem(row);
      return row.key;
    });
  }, [announceSummaryItem]);
  const clearSelection = useCallback(() => setSelectedItemKey(null), []);
  const selectedRow = selectedItemKey ? itemSummary.find(r => r.key === selectedItemKey) : null;

  // Focus-mode HUD is fixed and wraps onto several rows on narrow screens.
  // Publish its real height so the ticket grid starts below it, never under it.
  const hudRef = useRef(null);
  useEffect(() => {
    const el = hudRef.current;
    const root = document.documentElement;
    if (!focusMode || !el) return undefined;
    const publish = () => root.style.setProperty('--td-hud-h', `${Math.ceil(el.getBoundingClientRect().height)}px`);
    publish();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    window.addEventListener('resize', publish);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', publish);
      root.style.removeProperty('--td-hud-h');
    };
  }, [focusMode]);

  const viewEmoji = view === 'active' ? '🔴' : view === 'stale' ? '⏰' : '✅';

  return (
    <div className="kds__board" style={{ '--kds-text': textScale }}>
      {focusMode && createPortal(
        <div className="td__focus-corner" ref={hudRef}>
          <div className="td__hud-info">
            <button className="td__hud-view" onClick={() => setView('active')} disabled={view === 'active'}>
              {viewEmoji} {t(`kds.pill.${view}`)} <b>{visibleList.length}</b>
            </button>
            {view !== 'stale' && counts.stale > 0 && (
              <button className="td__hud-stale" onClick={() => setView('stale')} title={t('kds.pill.stale')}>⏰ {counts.stale}</button>
            )}
          </div>
          <div className="td__hud-controls">
            {onOpenDailyOps && (
              <button className="td__fz-btn td__fz-dops" onClick={onOpenDailyOps} title={t('kds.fz.dops')}>🧺</button>
            )}
            <button
              className={`td__fz-btn td__fz-help${boardHelpSent ? ' td__fz-help--sent' : ''}${boardHelpCooldown > 0 && !boardHelpSent ? ' td__fz-help--cooldown' : ''}`}
              onClick={handleBoardNeedHelp} disabled={boardHelpCooldown > 0} title={t('kds.fz.help')}>
              {boardHelpSent ? '✅' : boardHelpCooldown > 0 ? `🆘${boardHelpCooldown}s` : '🆘'}
            </button>
            <button className="td__fz-btn td__fz-refresh" onClick={refetch} disabled={loading} title={t('app.refresh')}>↻</button>
            <button className={`td__fz-btn td__fz-summary${showItemSummary ? ' td__fz-summary--on' : ''}`}
              onClick={() => setShowItemSummary(s => !s)} title={showItemSummary ? t('kds.fz.summaryHide') : t('kds.fz.summaryShow')}>📋</button>
            <Stepper variant="focus" ico="▦" label={t('kds.cols')} value={cols ?? t('kds.colsAuto')}
              onLess={() => stepCols(-1)} onMore={() => stepCols(1)} onReset={resetCols}
              lessTitle={t('kds.colsFewer')} moreTitle={t('kds.colsMore')} resetTitle={t('kds.colsReset')}
              lessDisabled={cols === COLS_MIN} moreDisabled={cols === COLS_MAX} />
            <Stepper variant="focus" ico="A" label={t('kds.text')} value={TEXT_STEPS[textIdx].label}
              onLess={() => stepText(-1)} onMore={() => stepText(1)} onReset={resetText}
              lessTitle={t('kds.textSmaller')} moreTitle={t('kds.textBigger')} resetTitle={t('kds.textReset')}
              lessDisabled={textIdx === 0} moreDisabled={textIdx === TEXT_STEPS.length - 1} />
            <button className="td__focus-exit" onClick={toggleFocus} title={t('kds.exitFocusTitle')}>✕ {t('kds.exit')}</button>
          </div>
        </div>,
        document.body
      )}

      <div className="kds__toolbar-sticky" style={focusMode ? { display: 'none' } : {}}>
        <SyncBar loading={loading} error={error} lastFetch={lastFetch} onRefresh={refetch}
          openCount={merged.filter(o => o.squareState === 'OPEN').length}
          updateSource={updateSource} pollInterval={pollMs ?? pollInterval} setPollInterval={handleSetPollInterval}
          autoMode={!pollPinned} onAutoPoll={handleAutoPoll}
          lanOnline={lanOnline} netOnline={netOnline} />

        <div className="kds__toolbar">
          <div className="kds__pills">
            {[
              { k: 'active', emoji: '🔴' },
              { k: 'stale', emoji: '⏰' },
              { k: 'completed', emoji: '✅' },
            ].map(({ k, emoji }) => (
              <button key={k}
                className={`kds__pill${view === k ? ' kds__pill--on' : ''}${k === 'stale' && counts.stale > 0 ? ' kds__pill--alert' : ''}`}
                onClick={() => setView(k)}>
                <span className="kds__pill-ico">{emoji}</span> <span className="kds__pill-lbl">{t(`kds.pill.${k}`)}</span>
                <span className="kds__pill-n">{counts[k]}</span>
              </button>
            ))}
          </div>
          <div className="kds__legend">
            {[{ b: 'fresh', l: '<5m' }, { b: 'warning', l: '5–15m' }, { b: 'medium', l: '15–20m' }, { b: 'urgent', l: '20–30m' }, { b: 'critical', l: '30–90m' }, { b: 'stale', l: '>90m' }].map(({ b, l }) => (
              <span key={b} className={`kds__leg kds__leg--${b}`}>{l}</span>
            ))}
          </div>
          <button
            className={`kds__chef-toggle${alertsEnabled && !alertsMuted ? ' kds__chef-toggle--on' : ''}`}
            onClick={toggleAlerts}
            title={
              !alertsEnabled ? t('kds.alerts.titleOff')
                : alertsMuted ? t('kds.alerts.titleMuted')
                  : t('kds.alerts.titleOn')
            }>
            {!alertsEnabled ? `🔕 ${t('kds.alerts.off')}` : alertsMuted ? `🔇 ${t('kds.alerts.muted')}` : `🔔 ${t('kds.alerts.on')}`}
          </button>
          <button className={`kds__chef-toggle${chefMode ? ' kds__chef-toggle--on' : ''}`}
            onClick={() => setChefMode(m => !m)} title={chefMode ? t('kds.chef.exitTitle') : t('kds.chef.enterTitle')}>
            {chefMode ? `👨‍🍳 ${t('kds.chef.on')}` : `👨‍🍳 ${t('kds.chef.off')}`}
          </button>
          {chefMode && (
            <button className={`kds__profile-btn${activeProfile ? ' kds__profile-btn--on' : ''}`}
              style={activeProfile ? { borderColor: activeProfile.color, color: activeProfile.color } : undefined}
              onClick={() => setShowProfileMgr(true)} title={t('kds.profileTitle')}>
              {activeProfile ? <><span className="kds__profile-dot" style={{ background: activeProfile.color }} />{activeProfile.name}</> : `👤 ${t('kds.profile')}`}
            </button>
          )}
          <button className={`kds__summary-toggle${showItemSummary ? ' kds__summary-toggle--on' : ''}`}
            onClick={() => setShowItemSummary(s => !s)} title={showItemSummary ? t('kds.summary.hideTitle') : t('kds.summary.showTitle')}>
            {showItemSummary ? `📋 ${t('kds.summary.on')}` : `📋 ${t('kds.summary.off')}`}
          </button>
          {chefMode && hiddenCount > 0 && (
            <button className={`kds__show-hidden-btn${showHidden ? ' kds__show-hidden-btn--on' : ''}`}
              onClick={() => setShowHidden(s => !s)}>
              {showHidden ? t('kds.hidden.showing', { n: hiddenCount }) : t('kds.hidden.count', { n: hiddenCount })}
            </button>
          )}
          {chefMode && hiddenCount > 0 && showHidden && (
            <button className="kds__unhide-all-btn" onClick={unhideAll}>{t('kds.unhideAll')}</button>
          )}
          <Stepper ico="▦" label={t('kds.cols')} value={cols ?? t('kds.colsAuto')}
            onLess={() => stepCols(-1)} onMore={() => stepCols(1)} onReset={resetCols}
            lessTitle={t('kds.colsFewer')} moreTitle={t('kds.colsMore')} resetTitle={t('kds.colsReset')}
            lessDisabled={cols === COLS_MIN} moreDisabled={cols === COLS_MAX} />
          <Stepper ico="A" label={t('kds.text')} value={TEXT_STEPS[textIdx].label}
            onLess={() => stepText(-1)} onMore={() => stepText(1)} onReset={resetText}
            lessTitle={t('kds.textSmaller')} moreTitle={t('kds.textBigger')} resetTitle={t('kds.textReset')}
            lessDisabled={textIdx === 0} moreDisabled={textIdx === TEXT_STEPS.length - 1} />
        </div>
      </div>

      {/* Outside the list/empty switch on purpose: when every finished order is
          older than the window the tab is empty, and this is the way back to them. */}
      {view === 'completed' && completedEarlier > 0 && (
        <div className="kds__cwin">
          <span className="kds__cwin-txt">
            {showAllCompleted
              ? t('kds.cwin.all', { n: completedAll.length })
              : t('kds.cwin.recent', { hours: windowHours, n: completedEarlier })}
          </span>
          <button className="kds__cwin-btn" onClick={() => setShowAllCompleted(v => !v)}>
            {showAllCompleted ? t('kds.cwin.showRecent', { hours: windowHours }) : t('kds.cwin.showAll')}
          </button>
        </div>
      )}

      {/* Orders cached from this device's last fetch show at once; the spinner
          is only for a board with nothing to show yet. */}
      {loading && !lastFetch && orders.length === 0 ? (
        <div className="kds__loader"><div className="kds__loader-spin" /><p>{t('kds.connecting')}</p></div>
      ) : showList.length === 0 ? (
        <div className="kds__empty">
          {view === 'active' && <><span>🎉</span><h3>{t('kds.empty.active.title')}</h3><p>{t('kds.empty.active.sub')}</p></>}
          {view === 'stale' && <><span>✨</span><h3>{t('kds.empty.stale.title')}</h3><p>{t('kds.empty.stale.sub')}</p></>}
          {view === 'completed' && <><span>📭</span><h3>{t('kds.empty.completed.title')}</h3><p>{t('kds.empty.completed.sub')}</p></>}
        </div>
      ) : (
        <>
          {showItemSummary && view === 'active' && itemSummary.length > 0 && (
            <div className="kds__item-summary">
              <div className="kds__item-summary__header">
                <span className="kds__item-summary__title">📋 {t('kds.summary.heading')}</span>
                <span className="kds__item-summary__meta">{t('kds.summary.meta', { tickets: active.length, items: itemSummary.reduce((s, r) => s + r.totalQty, 0) })}</span>
                {selectedRow && (
                  <span className="kds__item-summary__focus">
                    <span className="kds__item-summary__focus-ico">🔊</span>
                    <HighlightCountdown key={selectedItemKey} durationMs={HIGHLIGHT_AUTO_CLEAR_MS} onExpire={clearSelection} />
                    <button className="kds__item-summary__focus-clear" onClick={clearSelection} title={t('kds.summary.clear')}>✕</button>
                  </span>
                )}
                <button className="kds__item-summary__close" onClick={() => setShowItemSummary(false)}>✕</button>
              </div>
              <div className="kds__item-summary__compact">
                {itemSummary.map(row => {
                  const isMine = chefMode && activeProfile && itemMatchesProfile(row.displayName, activeProfile);
                  const isSelected = selectedItemKey === row.key;
                  const ticketList = row.tickets.map(tk => tk.label).join(', ');
                  return (
                    <span key={row.displayName}
                      role="button" tabIndex={0} aria-pressed={isSelected}
                      className={`kds__item-summary__chip${isMine ? ' kds__item-summary__chip--mine' : ''}${isSelected ? ' kds__item-summary__chip--selected' : ''}`}
                      style={isMine && activeProfile ? { '--mine-color': getItemColorForProfile(row.displayName, activeProfile) } : undefined}
                      onClick={() => handleSummaryItemClick(row)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSummaryItemClick(row); } }}
                      title={t('kds.summary.chipTitle', { n: row.tickets.length, list: ticketList })}>
                      {ln(row.displayName)}<em>{row.totalQty}</em>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {view === 'completed' && (
            <CompletedFilters filters={completedFilters} setFilters={setCompletedFilters}
              totalCount={completedShown.length} filteredCount={completed.length} />
          )}
          <div className="kds__grid" ref={gridRef}
            style={cols ? { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` } : undefined}>
            {visibleList.map(tk => {
              const { delayedItemIdxs, isDelayed } = delayFor.get(tk.id);
              const isHiddenCard = chefMode && hiddenTickets.has(tk.id);
              const isHighlighted = view === 'active' && highlightedTicketIds ? highlightedTicketIds.has(tk.id) : false;
              const isDimmed = view === 'active' && highlightedTicketIds ? !highlightedTicketIds.has(tk.id) : false;
              return (
                <div key={tk.id} className={isHiddenCard ? 'kds__card-hidden-wrap' : undefined}>
                  {isHiddenCard && <div className="kds__hidden-overlay">{t('kds.hiddenOverlay')}</div>}
                  {/* Props are kept stable (cached ticket, delay array, callbacks) so
                      the memoized card only re-renders when its own data changes. */}
                  <KdsCard ticket={tk} now={now} chefMode={chefMode} isDelayed={isDelayed}
                    delayedItemIdxs={delayedItemIdxs} activeProfile={activeProfile} broadcast={broadcast}
                    highlighted={isHighlighted} dimmed={isDimmed}
                    onHide={chefMode && !isHiddenCard ? hideTicket : undefined} />
                </div>
              );
            })}
          </div>
        </>
      )}
      {belowCount > 0 && (
        <button className="kds__more-chip" onClick={pageDown} title={t('kds.moreBelowTitle')}>
          <span className="kds__more-chip-ico" aria-hidden="true">▼</span>
          {t('kds.moreBelow', { n: belowCount })}
        </button>
      )}
      {showProfileMgr && (
        <ChefProfileManager profiles={chefProfiles} activeId={activeProfileId}
          onSelect={handleSelectProfile} onClose={() => setShowProfileMgr(false)} onChange={setChefProfiles} />
      )}
    </div>
  );
}
